/**
 * 认证状态（严格会话）
 * ------------------------------------------------------------
 * 会话由服务端 httpOnly Cookie 承载，前端不接触令牌、不写 localStorage。
 * 启动时用 /api/auth/me 恢复登录态；登录/登出/改密后同步内存用户。
 */
import { computed, ref } from 'vue'

/** 租户成员关系摘要（登录/me 返回，用于租户选择器与顶栏切换） */
export interface TenantBrief {
  id: string
  code: string
  name: string
  /** 租户状态：active / suspended / archived（非 active 不可进入） */
  status: string
  /** 成员状态：active / invited / disabled */
  memberStatus: string
  /** 租户内角色名（P1 仅展示） */
  roles: string[]
}

export interface AuthUser {
  personId: string
  accountId: string
  accountKind: string
  deptId: string | null
  perms: string[]
  account: string
  name: string
  /** 昵称（本人维护，可空）；人员展示按查看者偏好显示姓名或昵称 */
  nickname?: string
  roleIds: string[]
  mustChangePassword: boolean
  /** 会话当前绑定的租户；null/缺省 = 未绑定（多租户成员需先选择） */
  tenantId?: string | null
  /** 我的租户成员关系清单 */
  tenants?: TenantBrief[]
}

const user = ref<AuthUser | null>(null)
let loadPromise: Promise<void> | null = null

/**
 * 登录态引导结果：
 * pending     尚未向服务端确认
 * ready       已确认（登录或未登录均算确定答案）
 * unavailable 服务暂不可用（网络错误/5xx），结论未知——不当作未登录处理
 */
export type AuthBootState = 'pending' | 'ready' | 'unavailable'
const bootState = ref<AuthBootState>('pending')

export const authUser = user
export const authBoot = bootState
export const isLoggedIn = computed(() => !!user.value)
export const mustChangePassword = computed(() => user.value?.mustChangePassword === true)
/** 已登录但会话未绑定租户 → 须先进入租户选择页（多租户成员且无上次租户时发生） */
export const needsTenantSelection = computed(() =>
  !!user.value && user.value.mustChangePassword !== true && !user.value.tenantId)

/** 登录并写入内存态；失败抛错（message 可直接展示） */
export async function login(account: string, password: string, remember = false): Promise<AuthUser> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ account, password, remember }),
  })
  const json = await res.json().catch(() => null)
  if (!res.ok || json?.code !== 0) {
    throw new Error(json?.message || '登录失败，请稍后重试')
  }
  user.value = mergeTenant(json.data.user, json.data)
  bootState.value = 'ready'
  loadPromise = Promise.resolve()
  return user.value
}

/** 合并登录/改密响应中的租户信息到用户对象（me 接口为平铺结构，无需合并） */
function mergeTenant(u: AuthUser, data: any): AuthUser {
  return { ...u, tenantId: data?.currentTenantId ?? u?.tenantId ?? null, tenants: data?.tenants ?? u?.tenants ?? [] }
}

/** 切换会话绑定租户；成功后调用方负责整页刷新（彻底清 store/缓存） */
export async function switchTenant(tenantId: string): Promise<void> {
  const res = await fetch('/api/tenant/switch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ tenantId }),
  })
  const json = await res.json().catch(() => null)
  if (!res.ok || json?.code !== 0) {
    throw new Error(json?.message || '切换租户失败，请稍后重试')
  }
}

/** 登出：撤销服务端会话并清内存态 */
export async function logout(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' })
  } catch {
    /* 网络异常也要清本地态 */
  }
  user.value = null
  bootState.value = 'ready'
  loadPromise = Promise.resolve()
}

/** 清除本地登录态（401/会话过期时调用）；401 是确定答案，置 ready */
export function clearAuth(): void {
  user.value = null
  bootState.value = 'ready'
  loadPromise = null
}

/**
 * 从服务端恢复当前用户；未登录返回 null
 * ------------------------------------------------------------
 * 仅 401 视为确定答案（会话无效 → 清态）；网络错误/5xx 是服务暂不可用，
 * 保留内存登录态、标记 unavailable，由调用方给重试路径——
 * 不得把服务抖动显示成「登录已失效」。
 */
export async function fetchMe(): Promise<AuthUser | null> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'same-origin', cache: 'no-store' })
    if (res.status === 401) {
      user.value = null
      bootState.value = 'ready'
      return null
    }
    if (!res.ok) {
      bootState.value = 'unavailable'
      return user.value
    }
    const json = await res.json()
    user.value = (json?.data as AuthUser) ?? null
    bootState.value = 'ready'
    return user.value
  } catch {
    bootState.value = 'unavailable'
    return user.value
  }
}

/** 首次导航时恢复登录态（并发调用只发一次请求） */
export function ensureAuthLoaded(): Promise<void> {
  if (!loadPromise) loadPromise = fetchMe().then(() => undefined)
  return loadPromise
}

/** 服务恢复后重试登录态确认（unavailable 页面「重试」按钮用） */
export function retryAuth(): Promise<void> {
  loadPromise = null
  return ensureAuthLoaded()
}

/** 改密后更新内存用户 */
export function applyUser(next: AuthUser): void {
  user.value = next
}

/** 保存本人昵称（留空清除，展示回落姓名）；成功后同步内存用户 */
export async function saveNickname(nickname: string): Promise<AuthUser> {
  const res = await fetch('/api/auth/profile', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ nickname }),
  })
  const json = await res.json().catch(() => null)
  if (!res.ok || json?.code !== 0) {
    throw new Error(json?.message || '昵称保存失败，请稍后重试')
  }
  const next = mergeTenant(json.data as AuthUser, json.data)
  user.value = next
  return next
}
